import { Box } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import styles from './PatientsReassignment.module.scss';
import Modal from '~/ui/components/common/Modal';
import { useStoreActions } from '~/store/hooks';
import api from '~/services/api';
import { extractErrorMessage } from '~/utils/error';
import Loader from '~/ui/components/common/Loader';
import PatientsReassignmentForm from './PatientsReassignmentForm';
import { IOption } from '~/ui/components/inputs/Select/types';
import { IFormValues } from './types';
import { IHealthManagerSelector, IUserList } from '~/services/api/users/types';
import { defaultParams } from './constants';
import Button from '~/ui/components/common/Button';
import { IBulkReassignPatientsRequest } from '~/services/api/patients/types';

interface IProps {
  open: boolean;
  sourceUser: IUserList;
  onClose: () => void;
}

const PatientsReassignment: FC<IProps> = ({ sourceUser, open, onClose }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  const [sourceProgramIds, setSourceProgramIds] = useState<number[]>([]);
  const [healthManagers, setHealthManagers] = useState<IHealthManagerSelector[]>([]);

  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);

  const form = useForm<IFormValues>({
    defaultValues: {
      sourcePatients: [],
      targetPatients: [],
      sourceUserId: sourceUser.id,
      targetUserId: null,
      sourceSearch: '',
      targetSearch: '',
      initialTargetIds: [],
    },
  });

  const {
    setValue,
    reset,
    watch,
    handleSubmit,
    formState: { isDirty },
  } = form;

  const targetUserId = watch('targetUserId');

  const healthManagerOptions: IOption[] = useMemo(
    () =>
      healthManagers.filter(x => x.id !== sourceUser.id).map(x => ({ value: x.id, label: x.name })),
    [healthManagers, sourceUser.id],
  );

  useEffect(() => {
    if (targetUserId) {
      api.users.getPatients(targetUserId, defaultParams).then(result => {
        const patients = result.data;

        setValue('targetPatients', patients);

        setValue(
          'initialTargetIds',
          patients.map(x => x.id),
        );
      });
    } else {
      reset();
      setValue('targetPatients', []);
    }
  }, [targetUserId, setValue]);

  const loadSourcePatients = async () => {
    const leftPatients = await api.users
      .getPatients(sourceUser.id, defaultParams)
      .then(result => result.data);

    setValue('sourcePatients', leftPatients);
  };

  const onMount = async () => {
    setIsLoading(true);
    setSourceProgramIds([]);
    try {
      const [, programs] = await Promise.all([
        loadSourcePatients(),
        api.users.getAssociatedPrograms(sourceUser.id).then(result => result.data),
      ]);
      const programIds = programs.map(x => x.programId);
      setSourceProgramIds(programIds);

      const selectors = await api.users
        .getHealthManagerSelectors(programIds)
        .then(result => result.data);
      setHealthManagers(selectors || []);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  const selectedTargetManager = useMemo(
    () => healthManagers.find(x => x.id === targetUserId),
    [healthManagers, targetUserId],
  );

  const commonProgramIds = useMemo(() => {
    if (!selectedTargetManager) {
      return [];
    }

    const targetProgramIds = selectedTargetManager.programs.map(x => x.id);

    return sourceProgramIds.filter(x => targetProgramIds.includes(x));
  }, [sourceProgramIds, selectedTargetManager]);

  useEffect(() => {
    if (open) {
      reset();
      onMount();
    }
  }, [open, sourceUser]);

  const handleClose = () => {
    if (!isUpdating) {
      onClose();
    }
  };

  const onSubmit = async (data: IFormValues) => {
    try {
      setIsUpdating(true);

      const payload: IBulkReassignPatientsRequest = {
        sourceUserId: data.sourceUserId,
        targetUserId: data.targetUserId,
        sourcePatientIds: data.sourcePatients.map(x => x.id),
        targetPatientIds: data.targetPatients.map(x => x.id),
      };

      await api.patients.bulkReassign(payload);

      showNotify('Managers successfully updated');
      onClose();
    } catch (e) {
      showError(extractErrorMessage(e));
      setIsUpdating(false);
    }
  };

  return (
    <FormProvider {...form}>
      <Modal
        open={open}
        title="Patients Reassignment"
        onClose={onClose}
        maxWidth="md"
        fullWidth
        footer={
          <div className={styles.saveBtnWrapper}>
            <Button variant="outlined" color="primary" onClick={handleClose}>
              Cancel
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit(onSubmit)}
              isLoading={isUpdating}
              disabled={!isDirty}
            >
              Save
            </Button>
          </div>
        }
      >
        <Box className={styles.content}>
          {isLoading ? (
            <Loader />
          ) : (
            <PatientsReassignmentForm
              sourceUser={sourceUser}
              commonProgramIds={commonProgramIds}
              healthManagerOptions={healthManagerOptions}
            />
          )}
        </Box>
      </Modal>
    </FormProvider>
  );
};

export default PatientsReassignment;
